import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import Status from 'components/field/Status';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Collapse from 'components/global/Collapse';
import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import {
	faCheckCircle,
	faChevronDown,
	faChevronUp,
	faCircleCheck,
	faCircleInfo,
	faXmarkCircle
} from '@fortawesome/pro-solid-svg-icons';

import * as S from './OtomationTestForm.styled';

const OtomationTestForm = NiceModal.create(() => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					orderId: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const { showError } = useAlert();
	const [testResult, setTestResult] = useState();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderId: null,
			reverseShipment: false
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;

	const onSubmit = async (formData) => {
		try {
			const response = await axiosPrivate.post(SHIPPING_AUTOMATION.GET_ANALYSE_AUTOMATION_RUES_TEST, {
				ID: formData.orderId,
				reverseShipment: formData.reverseShipment
			});
			const { data, status } = response;
			if (status === 200) {
				setTestResult(data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error?.response?.data?.code || error?.response?.data?.message
			});
		}
	};

	useEffect(() => {
		if (watch('orderId') === null || (watch('orderId') !== null && !isLoading)) {
			setTestResult(null);
		}
	}, [watch('orderId')]);

	const orderIdAutocompleteColumns = useMemo(() => [{ accessor: 'orderID', width: 200 }], []);

	return (
		<Modal fullwidth maxWidth="x-large">
			<ModalTitle>
				<Typography variant="p1">otomationTest</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="orderNo" isLoading={isLoading} required>
						<AutoComplete
							name="orderId"
							autoCompleteName={AUTOCOMPLETE.ORDERS}
							columns={orderIdAutocompleteColumns}
							selectionLabelAccessor={['orderID']}
							value={watch('orderId')}
							setValue={(value) => {
								setValue('orderId', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
								setTestResult(null);
							}}
						/>
					</FormItem>
					<FormItem label="reverseShipment" isLoading={isLoading}>
						<SwitchInput className="mt-3" name="reverseShipment" />
					</FormItem>

					{testResult && (
						<S.ResultContainer>
							<Typography variant="p3" fontWeight="500">
								results
							</Typography>
							{testResult?.analysedRules?.length > 0 ? (
								testResult?.analysedRules?.map((testData, index) => (
									<TestResultCard
										key={testData?.id}
										index={index + 1}
										name={testData?.name}
										method={testData?.method}
										status={testData?.status}
										analysedConditions={testData?.analysedConditions}
										analysedDeliveryCompanies={testData?.analysedDeliveryCompanies}
										result={testData?.result}
									/>
								))
							) : (
								<Typography variant="p3" fontWeight="500">
									testAutomation.noResults
								</Typography>
							)}
						</S.ResultContainer>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button variant="outline" label="cancel" onClick={() => modal.remove(OtomationTestForm)} />
				<Button
					loading={isLoading}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty || watch('orderId') === null}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="test"
				/>
			</ModalActions>
		</Modal>
	);
});

const TestResultCard = ({ index, name, method, status, analysedConditions, analysedDeliveryCompanies, result }) => {
	const [isCardExpanded, setIsCardExpanded] = useState(false);

	const handleCardClick = () => {
		setIsCardExpanded((prev) => !prev);
	};

	return (
		<S.TestResultWrapper>
			<S.TestResultContainer onClick={handleCardClick} isExpanded={isCardExpanded}>
				<S.TitleRowContainer className="order-row">
					{result ? (
						<Icon icon={faCheckCircle} color="green" fontSize="1.8rem" />
					) : (
						<Icon icon={faXmarkCircle} color="red" fontSize="1.8rem" />
					)}
					<Typography variant="p3" fontWeight="700">
						{index}
					</Typography>
				</S.TitleRowContainer>
				<S.TitleRowContainer>
					<Typography variant="p3">ruleName</Typography>
					<Typography variant="p3" fontWeight="700" noWrap>
						{name}
					</Typography>
				</S.TitleRowContainer>
				<S.TitleRowContainer>
					<Typography variant="p3">assignmentPriorityMethod</Typography>
					<Typography variant="p3" fontWeight="700" noWrap>
						{method}
					</Typography>
				</S.TitleRowContainer>
				<S.TitleRowContainer>
					<Typography variant="p3">status</Typography>
					<Status
						value={status}
						style={{
							height: 'unset',
							padding: 'var(--padding-1) var(--padding-2)'
						}}
					/>
				</S.TitleRowContainer>
				<S.TitleRowContainer>
					<Typography variant="p3">testResult</Typography>
					<Status
						value={result ? 'otomationTestSuccess' : 'otomationTestFailed'}
						style={{
							height: 'unset',
							padding: 'var(--padding-1) var(--padding-2)'
						}}
					/>
				</S.TitleRowContainer>
				{isCardExpanded ? <Icon icon={faChevronUp} /> : <Icon icon={faChevronDown} />}
			</S.TestResultContainer>

			<Collapse visible={isCardExpanded} invisibleMode defaultVisible>
				<S.TestDetailsContainer isExpanded={isCardExpanded} className={result ? 'success' : 'failed'}>
					<S.ColContainer>
						<Typography variant="p3">conditions</Typography>
						{analysedConditions?.map?.((condition, idx) => (
							<S.TestResultContent key={`${condition.conditionStr}-${idx}`}>
								{condition.result === 'successful' ? (
									<Icon icon={faCircleCheck} color="green" />
								) : condition.result === 'na' ? (
									<Icon icon={faCircleInfo} color="var(--gray)" fontSize="1.8rem" />
								) : (
									<Icon icon={faXmarkCircle} color="red" />
								)}
								<Typography noWrap variant="p3" fontWeight="900">
									{condition.conditionStr}
								</Typography>
							</S.TestResultContent>
						))}
					</S.ColContainer>
					<S.ColContainer>
						<Typography variant="p3">locationFilters</Typography>
						{analysedDeliveryCompanies?.map?.((dc, idx) => (
							<S.TestResultContent key={`${dc.dcSettingsId}-${idx}`}>
								{dc?.result === 'available' ? (
									<Icon icon={faCircleCheck} color="green" />
								) : (
									<Icon icon={faXmarkCircle} color="red" />
								)}
								<Typography noWrap variant="p3" fontWeight="900">
									{dc.dcName}
								</Typography>
							</S.TestResultContent>
						))}
					</S.ColContainer>
					<S.ColContainer>
						<Typography variant="p3">locationResult</Typography>
						{analysedDeliveryCompanies?.map?.((dc, idx) => (
							<S.TestResultContent key={`${dc.dcSettingsId}-${idx}`}>
								<Typography
									noWrap
									variant="p3"
									fontWeight="900"
									color={dc?.result === 'available' ? 'var(--green)' : 'var(--red)'}>
									{dc.result}
								</Typography>
							</S.TestResultContent>
						))}
					</S.ColContainer>
				</S.TestDetailsContainer>
			</Collapse>
		</S.TestResultWrapper>
	);
};

export default OtomationTestForm;
